import * as i0 from '@angular/core';
import { Injectable, APP_INITIALIZER, NgModule } from '@angular/core';
import { CollectionRepository } from '@engineering11/web-api-firebase';
import { HttpClient } from '@angular/common/http';
import { getAuth } from '@angular/fire/auth';
import { retryPromise } from '@engineering11/utility';
import { e11Error } from '@engineering11/web-api-error';
import { Subject, firstValueFrom } from 'rxjs';
import * as i1 from '@engineering11/web-utilities';
import * as i2 from '@angular/platform-browser';
import * as i3 from '@angular/router';

const DisplayNameRegex = /^[a-zA-Z]([a-zA-Z0-9-]{4,20})$/;
const DomainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;
const HostRegex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/;

var TenantType;
(function (TenantType) {
    TenantType["Business"] = "Business";
    TenantType["Consumer"] = "Consumer";
})(TenantType || (TenantType = {}));

class TenantBootstrapRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = `bootstrap_tenant/`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class BaseTenantBootstrapConfigProvider {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseTenantBootstrapConfigProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseTenantBootstrapConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseTenantBootstrapConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const API_NAME = 'tenant-bootstrap';
const TENANT_BOOTSTRAP_ERRORS = {
    CONFIG_MISSING: `${API_NAME}/config-missing`,
    BOOTSTRAP_DATA_MISSING: `${API_NAME}/bootstrap-data-missing`,
};

const checkItIsHex = (value) => {
    return /^#[0-9A-Fa-f]{6}$/.test(value);
};
const checkIsRgbString = (value) => {
    return /^\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*$/.test(value);
};
const checkItIsRgb = (value) => {
    return value?.length === 3;
};
const parseHex = (val) => {
    const intermediate = val.replace('#', '').match(/.{1,2}/g);
    if (!checkItIsRgb(intermediate)) {
        throw new Error();
    }
    return intermediate;
};
const hexToRGBString = (aRgbHex) => {
    const aRgb = [parseInt(aRgbHex[0], 16), parseInt(aRgbHex[1], 16), parseInt(aRgbHex[2], 16)];
    const rgbString = aRgb.toString();
    if (!checkIsRgbString(rgbString)) {
        throw new Error();
    }
    return rgbString;
};
const convertToRgb = (color) => {
    if (!checkItIsHex(color)) {
        return;
    }
    try {
        return hexToRGBString(parseHex(color));
    }
    catch (error) {
        return;
    }
};

var FAV_ICON_TYPES;
(function (FAV_ICON_TYPES) {
    FAV_ICON_TYPES[FAV_ICON_TYPES["STANDARD"] = 0] = "STANDARD";
    FAV_ICON_TYPES[FAV_ICON_TYPES["NOTIFICATION"] = 1] = "NOTIFICATION";
})(FAV_ICON_TYPES || (FAV_ICON_TYPES = {}));
class BrandBootstrapService {
    constructor(faviconService, titleService) {
        this.faviconService = faviconService;
        this.titleService = titleService;
    }
    setFavIcon(brand, type) {
        const favIconURI = type === FAV_ICON_TYPES.NOTIFICATION ? brand.favIconNotificationUrl : brand.favIconUrl;
        this.faviconService.setFavicon({ href: favIconURI, type: 'image/x-icon' });
    }
    setAppTitle(title) {
        this.titleService.setTitle(title);
    }
    setTheme(theme) {
        Object.keys(theme).forEach((themeVar) => {
            const themeKey = themeVar;
            const color = convertToRgb(theme[themeKey]);
            if (!color) {
                return;
            }
            document.documentElement.style.setProperty(themeVar, color);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BrandBootstrapService, deps: [{ token: i1.FaviconService }, { token: i2.Title }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BrandBootstrapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BrandBootstrapService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.FaviconService }, { type: i2.Title }] });

const appConfigInitializerFn = (appConfig) => {
    return () => {
        return appConfig.loadAppConfig();
    };
};
class AppConfigService {
    constructor(injector, configProvider, brandBootstrapService, router) {
        this.injector = injector;
        this.configProvider = configProvider;
        this.brandBootstrapService = brandBootstrapService;
        this.router = router;
        this.basePath = this.configProvider.config.appConfigBaseURL;
        this.host = location.host;
        this.appConfig$ = new Subject();
        this.shouldRetry = (error, _attemptNumber) => {
            if (!location.pathname.includes(this.configProvider.config.notFoundRoute))
                this.router.navigate([this.configProvider.config.notFoundRoute]);
            return error.status !== 404;
        };
    }
    loadAppConfig() {
        let http = this.injector.get(HttpClient);
        const loadConfigPromise = () => firstValueFrom(http.get(`${this.buildPath()}/bootstrap.json`));
        const retryOpts = {
            promiseFunction: () => loadConfigPromise(),
            shouldRetry: this.shouldRetry,
            maxRetries: 5,
            delayMs: 1000,
        };
        return retryPromise(retryOpts)
            .then((data) => {
            if (!data)
                throw e11Error({ type: TENANT_BOOTSTRAP_ERRORS.BOOTSTRAP_DATA_MISSING, title: 'Bootstrap data missing' });
            const { theme } = data;
            this.appConfig = { ...data, isDefaultTenant: this.configProvider.config.defaultTenant === data.auth.host };
            this.appConfig$.next(this.appConfig);
            this.brandBootstrapService.setTheme(theme);
            this.brandBootstrapService.setAppTitle(data.brand.appName);
            this.setFavIcon(FAV_ICON_TYPES.STANDARD);
            this.setTenant(data.auth.tenantId);
            // no error but we are on this.configProvider.config.notFoundRoute route to /
            if (location.hash.includes(this.configProvider.config.notFoundRoute))
                this.router.navigate(['/']);
            return data;
        })
            .catch((e) => {
            console.error(e);
            this.appConfig = {
                auth: {
                    tenantId: 'unknown',
                },
            };
        });
    }
    get config() {
        if (!this.appConfig)
            throw e11Error({
                type: TENANT_BOOTSTRAP_ERRORS.CONFIG_MISSING,
                title: 'Tenant bootstrap config not loaded',
                message: 'Check the readme of @engineering11/tenant-bootstrap-web for instructions',
                level: 'none',
            });
        return this.appConfig;
    }
    setFavIcon(type) {
        this.brandBootstrapService.setFavIcon(this.appConfig.brand, type);
    }
    setTenant(tenantId) {
        // NOTICE: This is a workaround for the issue with the firebase auth tenantId not being set,
        // for firebase.auth not a function error
        getAuth().tenantId = tenantId;
        // firebase.auth().tenantId = tenantId
    }
    buildPath() {
        if (this.configProvider.config.host) {
            return `${this.basePath}/${this.configProvider.config.host}`;
        }
        if (!this.configProvider.config.hostPattern.test(this.host)) {
            return `${this.basePath}/${this.host}`;
        }
        return `${this.basePath}/${this.configProvider.config.defaultTenant}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AppConfigService, deps: [{ token: i0.Injector }, { token: BaseTenantBootstrapConfigProvider }, { token: BrandBootstrapService }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AppConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AppConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: BaseTenantBootstrapConfigProvider }, { type: BrandBootstrapService }, { type: i3.Router }] });

class TenantBootstrapModule {
    static forRoot(rootConfig) {
        const appInitializer = { provide: APP_INITIALIZER, useFactory: appConfigInitializerFn, multi: true, deps: [AppConfigService] };
        const providers = [{ provide: BaseTenantBootstrapConfigProvider, useClass: rootConfig.configProvider }];
        return {
            ngModule: TenantBootstrapModule,
            providers: rootConfig.suppressAppInitialization ? providers : [...providers, appInitializer],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TenantBootstrapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [],
                    declarations: [],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AppConfigService, BaseTenantBootstrapConfigProvider, BrandBootstrapService, DisplayNameRegex, DomainRegex, FAV_ICON_TYPES, HostRegex, TenantBootstrapModule, TenantBootstrapRepository, TenantType, appConfigInitializerFn };
